import React, { memo } from 'react';
import { useTheme } from '@mui/material';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import {
    ABSENCE_COST,
    ORG_CHART_HR_NAME,
} from 'routes/HumanResourcesModule/business/definitions';
import {
    Absence,
    DateString,
} from 'routes/HumanResourcesModule/business/types';
import HrChart from 'routes/HumanResourcesModule/components/HrChart/HrChart';
import {
    formatDataForDatedBarGraph,
    getDatedBarGraphOptions,
} from 'routes/HumanResourcesModule/PayState/PayStateSynthesis/helpers/eChartsHelpers';
import {
    getDateColumnsForVentilation,
    isColumnFromLastYearToggle,
} from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/gridHelpers';

type Props = {
    exercice: number;
    cardHeaderActions: React.ReactNode;
    absenceData: Absence[];
    dateColumns: DateString[];
    isLoading: boolean;
    isMonthly: boolean;
    showLastYearColumns: boolean;
};

const AbsenceGraph = ({
    exercice,
    cardHeaderActions,
    absenceData,
    dateColumns,
    isLoading,
    isMonthly,
    showLastYearColumns,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const theme = useTheme();

    const dates = getDateColumnsForVentilation(dateColumns, isMonthly);
    const valueField = ABSENCE_COST;
    const groupField = ORG_CHART_HR_NAME;
    const vizData = formatDataForDatedBarGraph<Absence>(
        absenceData,
        dates,
        groupField,
        valueField,
    );
    const filteredDates = showLastYearColumns
        ? dates
        : dates.filter(
              (date) => !isColumnFromLastYearToggle(date, isMonthly, exercice),
          );

    const options = getDatedBarGraphOptions({
        vizData,
        groupField,
        valueField,
        filteredDates,
        isMonthly,
        exercice,
        theme,
        t,
    });

    return (
        <HrChart
            title={t('humanResources.absences')}
            isLoading={isLoading}
            cardHeaderActions={cardHeaderActions}
            options={options}
        />
    );
};

export default memo(AbsenceGraph, _.isEqual);
